import { type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Trash2,
  Eye,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { format } from "date-fns";
import { formatDateTime2 } from "@/utils/format-date";
import {
  type DtefImportHistoryItem,
  type DtefImportHistoryOrderBy,
  type DtefImportHistoryOrderDir,
} from "@/services/conciliation/dtfe-import";

interface ColumnOptions {
  vigenteBatchId?: string;
  orderBy: DtefImportHistoryOrderBy;
  orderDir: DtefImportHistoryOrderDir;
  onSort: (field: DtefImportHistoryOrderBy) => void;
  onDelete: (batchId: string) => void;
  onViewDetails: (batchId: string) => void;
}

const STATUS_CONFIG: Record<
  string,
  { color: string; label: string; description: string }
> = {
  vigente: {
    color: "bg-green-500",
    label: "Vigente",
    description: "Lote mais recente e ativo — é o utilizado nas conciliações.",
  },
  active: {
    color: "bg-emerald-500",
    label: "Ativo",
    description: "Registros do lote ainda estão disponíveis no sistema.",
  },
  deleted: {
    color: "bg-red-400",
    label: "Excluído",
    description: "Registros deste lote foram removidos (soft delete).",
  },
};

const SortIndicator = ({
  field,
  orderBy,
  orderDir,
}: {
  field: DtefImportHistoryOrderBy;
  orderBy: DtefImportHistoryOrderBy;
  orderDir: DtefImportHistoryOrderDir;
}) => {
  if (orderBy !== field) {
    return <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />;
  }
  return orderDir === "asc" ? (
    <ArrowUp className="ml-1 h-3 w-3" />
  ) : (
    <ArrowDown className="ml-1 h-3 w-3" />
  );
};

const SortableHeader = ({
  field,
  label,
  orderBy,
  orderDir,
  onSort,
}: {
  field: DtefImportHistoryOrderBy;
  label: string;
  orderBy: DtefImportHistoryOrderBy;
  orderDir: DtefImportHistoryOrderDir;
  onSort: (field: DtefImportHistoryOrderBy) => void;
}) => (
  <button
    type="button"
    onClick={() => onSort(field)}
    className="inline-flex items-center text-sm font-medium hover:text-[#613F4F]"
  >
    {label}
    <SortIndicator field={field} orderBy={orderBy} orderDir={orderDir} />
  </button>
);

export function getImportHistoryColumns({
  vigenteBatchId,
  orderBy,
  orderDir,
  onSort,
  onDelete,
  onViewDetails,
}: ColumnOptions): ColumnDef<DtefImportHistoryItem>[] {
  return [
    {
      accessorKey: "created_at",
      header: () => (
        <SortableHeader
          field="created_at"
          label="Data/Hora"
          orderBy={orderBy}
          orderDir={orderDir}
          onSort={onSort}
        />
      ),
      cell: ({ row }) => {
        try {
          return format(new Date(row.original.created_at), "dd/MM/yyyy HH:mm");
        } catch {
          return row.original.created_at;
        }
      },
    },
    {
      id: "type",
      header: "Tipo",
      cell: ({ row }) =>
        row.original.entity_type === "PIX_DTEF_IMPORT"
          ? "PIX"
          : "Crédito/Débito",
    },
    {
      accessorKey: "email",
      header: () => (
        <SortableHeader
          field="email"
          label="Usuário"
          orderBy={orderBy}
          orderDir={orderDir}
          onSort={onSort}
        />
      ),
    },
    {
      id: "fileName",
      header: "Arquivo",
      cell: ({ row }) =>
        row.original.event_payload?.metadata?.fileName || "-",
    },
    {
      id: "rowCount",
      header: "Linhas",
      cell: ({ row }) => row.original.event_payload?.total_items ?? "-",
    },
    {
      id: "action",
      header: "Ação",
      cell: ({ row }) => {
        const actionMap: Record<string, string> = {
          replace: "Substituição",
          append: "Inclusão",
        };
        const action = row.original.event_payload?.metadata?.action;
        return actionMap[action ?? ""] ?? action ?? "-";
      },
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        const { is_active, entity_id } = row.original;
        const isVigente = entity_id === vigenteBatchId;
        const statusKey = !is_active
          ? "deleted"
          : isVigente
            ? "vigente"
            : "active";
        const cfg = STATUS_CONFIG[statusKey];

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1.5 cursor-help">
                  <span className={`h-2 w-2 rounded-full ${cfg.color}`} />
                  <span
                    className={
                      statusKey === "deleted"
                        ? "text-red-500"
                        : "text-green-700 font-medium"
                    }
                  >
                    {cfg.label}
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div>{cfg.description}</div>
                {statusKey === "deleted" && row.original.deleted_by && (
                  <div className="mt-1 text-xs text-gray-300">
                    Excluído por <strong>{row.original.deleted_by}</strong>
                    {row.original.deleted_at && (
                      <> em {formatDateTime2(row.original.deleted_at, { includeSeconds: true })}</>
                    )}
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      id: "details",
      header: "Detalhes",
      cell: ({ row }) => {
        const { is_active, entity_id } = row.original;
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewDetails(entity_id)}
                    disabled={!is_active}
                  >
                    <Eye
                      className={`h-4 w-4 text-[#613F4F] ${!is_active ? "opacity-30" : ""}`}
                    />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {is_active
                  ? "Ver detalhes por loja deste lote"
                  : "Lote excluído — detalhes indisponíveis"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      id: "delete",
      header: "Excluir",
      cell: ({ row }) => {
        const { is_active, entity_id } = row.original;
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => onDelete(entity_id)}
                    disabled={!is_active}
                  >
                    <Trash2
                      className={`h-4 w-4 ${!is_active ? "opacity-30" : ""}`}
                    />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {is_active ? "Excluir lote" : "Lote já excluído"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
  ];
}
